/**
 * Чернетка подання для екранів подань — списку (`/l/$view`) і дошки (`/b/$view`): одна машина
 * стану, щоб правило «що не йде в адресу» (SEC-110) не розходилося між екранами (views.md, розд. 6).
 *
 * - **Звідки.** Незбережені зміни — з `?d=` (FLT-08), той самий розбір, що й у `loader` маршруту.
 * - **Що не йде в адресу.** Фільтр з полем `personal` / `sensitive` чи задовгий код тримається в
 *   стані екрана (`held`), поки адреса — та сама. Список і дошка кладуть таку чернетку на сервер і
 *   переходять на `?state=<ключ>`; не вдалося (чи подання без id) — фільтр лише в цій вкладці, людина
 *   бачить тост `filterNotInLink`.
 * - **Зіпсоване посилання** — чисте подання і пояснення `viewLinkStale`, без тихої підміни.
 */
import type { FilterNode, MetaEntity, MetaView, ViewState } from '@clientline/contracts';
import { type UseQueryResult, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { filters as tf } from '../i18n/uk/filters.ts';
import { list as tl } from '../i18n/uk/list.ts';
import { toast } from '../ui/notify/store.ts';
import { ApiProblem } from './api.ts';
import { filterDraft } from './filters/tree-edit.ts';
import {
  addressable,
  type DecodedDraft,
  decodeDraft,
  encodeDraft,
  type ViewDraft,
} from './view-draft.ts';
import { saveViewState, stateDraft, viewStateQuery } from './view-state.ts';

export const NO_DRAFT: ViewDraft = {};

export interface AddressDraft {
  /**
   * Розібрана чернетка: з `?d=` — одразу, зі стану на сервері (`?state=`) — коли він прочитався.
   * `undefined` — стан ще в дорозі чи збій не `404` (екран показує скелет чи повтор).
   */
  decoded: DecodedDraft | undefined;
  /** Запит стану на сервері (вимкнений без `?state=`): помилка й повтор для екрана. */
  fromState: UseQueryResult<ViewState, unknown>;
}

/**
 * Незбережені зміни з адреси екрана подання — списку й дошки (FLT-08, views.md, розд. 6): `?d=`
 * розбирається синхронно, `?state=` — читанням стану на сервері, той самий розбір, що в `loader`.
 * `404` (чужий, прострочений, невідомий стан) — зіпсоване посилання, як зіпсований `d`.
 */
export function useAddressDraft(
  viewId: string | undefined,
  d: string | undefined,
  state: string | undefined,
): AddressDraft {
  const fromState = useQuery({ ...viewStateQuery(state ?? ''), enabled: state !== undefined });
  const decoded = useMemo<DecodedDraft | undefined>(() => {
    if (state === undefined) return decodeDraft(d);
    if (fromState.data) return stateDraft(fromState.data, viewId);
    return fromState.error instanceof ApiProblem && fromState.error.status === 404
      ? { ok: false }
      : undefined;
  }, [state, d, fromState.data, fromState.error, viewId]);
  return { decoded, fromState };
}

/** Екран подання: список або дошка; дошка зберігає в адресі обрану воронку. */
export type ViewDraftScreen = { route: '/l/$view' } | { route: '/b/$view'; pipeline?: string };

/** Фільтр, що не може йти в адресу: тримається, поки адреса — та сама. */
interface HeldFilter {
  d: string | undefined;
  filter: FilterNode | null | undefined;
  q: string | undefined;
}

export interface ViewDraftState {
  /** Чернетка: з адреси, а фільтр, що не йде в адресу, — зі стану екрана. */
  draft: ViewDraft;
  /** Фільтр чернетки тримається в стані екрана (в адресі його немає). */
  held: boolean;
  /** Відкрити подання; те саме — заміною запису історії, інше — новим. */
  open: (key: string, next?: ViewDraft) => void;
}

export function useViewDraft(
  view: MetaView,
  entity: MetaEntity,
  d: string | undefined,
  screen: ViewDraftScreen,
  /**
   * Чернетка зі стану на сервері (`?state=`, views.md, розд. 6), уже розібрана екраном
   * (`useAddressDraft`). Без неї — розбір `d`.
   */
  fromState?: { state: string; decoded: DecodedDraft },
): ViewDraftState {
  const navigate = useNavigate();
  const client = useQueryClient();
  const fromD = useMemo(() => decodeDraft(d), [d]);
  const decoded = fromState ? fromState.decoded : fromD;
  const [held, setHeld] = useState<HeldFilter | null>(null);
  // Фільтр у пам'яті діє, поки адреса та сама: з `?state=` чернетка вже вся на сервері.
  const heldNow = held !== null && fromState === undefined && held.d === d;
  const draft = useMemo<ViewDraft>(() => {
    const fromUrl = decoded.ok ? decoded.draft : NO_DRAFT;
    if (!heldNow || !held) return fromUrl;
    return {
      ...fromUrl,
      ...(held.filter !== undefined ? { filter: held.filter } : {}),
      ...(held.q ? { q: held.q } : {}),
    };
  }, [decoded, held, heldNow]);
  // Остання дія людини: відповідь запису стану, що прийшла після нової дії, — не застосовується. Пішла
  // людина з екрана (картка, інший розділ) — теж: перехід на `?state=` повернув би її на список.
  const opened = useRef(0);
  const router = useRouter();
  const { route } = screen;
  const pipeline = screen.route === '/b/$view' ? screen.pipeline : undefined;
  const open = useCallback(
    (key: string, next: ViewDraft = NO_DRAFT) => {
      const ticket = ++opened.current;
      const keep =
        key === view.key &&
        (next.filter !== undefined || Boolean(next.q)) &&
        !addressable(next, entity);
      const { filter, q, ...rest } = next;
      const encoded = encodeDraft(keep ? rest : next);
      setHeld(keep ? { d: encoded, filter, q } : null);
      const notInLink = () => toast({ text: tf.filterNotInLink, key: 'filter-not-in-link' });
      if (keep && !view.id) notInLink();
      else if (keep && view.id) {
        saveViewState(client, { id: view.id, entity: entity.key }, next).then(
          (id) => {
            if (ticket !== opened.current) return;
            const here = router.buildLocation({ to: route, params: { view: key } }).pathname;
            if (router.state.location.pathname !== here) return;
            // Воронка — поточна: людина могла перемкнути її, поки йшов запис.
            const now = (router.state.location.search as { pipeline?: string }).pipeline;
            const moved =
              route === '/b/$view'
                ? navigate({
                    to: '/b/$view',
                    params: { view: key },
                    search: { ...(now ? { pipeline: now } : {}), state: id },
                    replace: true,
                  })
                : navigate({
                    to: '/l/$view',
                    params: { view: key },
                    search: { state: id },
                    replace: true,
                  });
            // Умову тримаємо до нової адреси: без неї екран на кадр показав би подання без умови.
            void moved.then(() => setHeld(null));
          },
          () => {
            if (ticket === opened.current) notInLink();
          },
        );
      }
      const replace = key === view.key;
      const withD = encoded ? { d: encoded } : {};
      if (route === '/b/$view') {
        void navigate({
          to: '/b/$view',
          params: { view: key },
          search: { ...(pipeline ? { pipeline } : {}), ...withD },
          replace,
        });
      } else {
        void navigate({ to: '/l/$view', params: { view: key }, search: withD, replace });
      }
    },
    [client, navigate, router, view.key, view.id, entity, route, pipeline],
  );
  // Зіпсоване чи застаріле посилання: чисте подання і пояснення, без тихої підміни.
  useEffect(() => {
    if (decoded.ok) return;
    toast({ text: tl.viewLinkStale, key: `view-link-stale:${view.key}` });
    open(view.key);
  }, [decoded.ok, open, view.key]);
  return { draft, held: heldNow, open };
}

/**
 * Чернетка з новим фільтром: `base` — умова подання (`undefined` — конфігурація ще в дорозі, тоді
 * фільтр іде як є); фільтр, що збігся з поданням, із чернетки зникає (`filterDraft`).
 */
export function withFilterDraft(
  shown: ViewDraft,
  next: FilterNode | null,
  base: { filter: FilterNode | undefined } | undefined,
): ViewDraft {
  const { filter: _old, ...rest } = shown;
  const nextFilter = base ? filterDraft(next, base.filter) : next;
  return nextFilter === undefined ? rest : { ...rest, filter: nextFilter };
}
